import { useEffect, useRef, useState, Children, cloneElement, isValidElement } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Clock, FileCheck } from "lucide-react";
import PageSeo from "@/components/PageSeo";
import { TRADES, TRADE_NAMES, CA_OPTIONS, SALARIES_OPTIONS } from "@/data/content";
import { TARIFS, formatRange, formatMonthlyRange } from "@/data/tarifs";
import { trackEvent } from "@/lib/analytics";
import { postToApi, submitErrorMessage, warmBackend } from "@/lib/api";
import useSlowHint, { SLOW_HINT } from "@/lib/useSlowHint";
import { hy } from "@/lib/hyphenate";

const STEP_ANIMATION = "animate-in fade-in slide-in-from-right-4 duration-300";

function IndicativePrice({ activite }) {
  const trade = TRADES.find((t) => t.name === activite);
  const range = trade && TARIFS[trade.slug];
  if (!range) return null;
  return (
    <div className="mt-8 border-l-4 border-accent bg-white p-5" data-testid="devis-indicative-price" aria-live="polite">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Repère indicatif — {trade.name}</p>
      <p className="mt-1 font-display text-2xl font-extrabold tracking-tight">
        {formatRange(range)} <span className="text-sm font-semibold text-ink/60">/ an</span>
        <span className="ml-3 text-base font-bold text-ink/70">≈ {formatMonthlyRange(range)} / mois</span>
      </p>
      <p className="mt-2 text-xs leading-relaxed text-ink/65">Fourchette communiquée par le cabinet pour un contrat RC Pro + décennale. Votre tarif réel dépend de votre dossier : terminez la demande pour l’obtenir.</p>
    </div>
  );
}

const ACTIVITES = [...TRADE_NAMES, "Autre activité du BTP"];

const EXPERIENCE_OPTIONS = [
  "Moins de 3 ans",
  "3 à 10 ans",
  "Plus de 10 ans",
  "En création / reprise d'activité",
];

const ANTECEDENTS_OPTIONS = [
  "Non, aucun",
  "Oui — sinistre(s)",
  "Oui — résiliation par un assureur",
  "Oui — sinistre(s) et résiliation",
];

const SANS_DECENNALE_OPTIONS = [
  { value: "oui", label: "Oui" },
  { value: "non", label: "Non" },
  { value: "creation", label: "Société en création" },
];

const STEP_LABELS = ["Votre métier", "Votre profil", "Vos coordonnées"];

const initialForm = {
  nom_entreprise: "",
  activite: "",
  chiffre_affaires: "",
  experience: "",
  antecedents: "",
  nb_salaries: "",
  sans_decennale: "",
  adresse: "",
  code_postal: "",
  ville: "",
  civilite: "M.",
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  accepte_cgu: false,
};

const inputCls =
  "w-full rounded-none border-b-2 border-ink/40 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-ink/30 focus:border-accent";

function Field({ label, required, error, children, testId }) {
  return (
    <div>
      <label id={`${testId}-label`} htmlFor={testId} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">
        {label}
        {required && <span className="text-accent"> *</span>}
      </label>
      {Children.map(children, child => isValidElement(child) && ["input", "select", "textarea"].includes(child.type) ? cloneElement(child, { id: testId, "aria-required": required || undefined, "aria-invalid": !!error, "aria-describedby": error ? `${testId}-error` : undefined }) : child)}
      {error && (
        <p id={`${testId}-error`} role="alert" data-testid={`${testId}-error`} className="mt-1.5 font-mono text-[11px] uppercase tracking-wide text-accent">
          {error}
        </p>
      )}
    </div>
  );
}

export default function Devis() {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({
    ...initialForm,
    activite: location.state?.activite || "",
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const slow = useSlowHint(loading);
  const contactTracked = useRef(false);

  // Réveille le serveur de formulaires pendant la saisie.
  useEffect(() => {
    warmBackend();
  }, []);

  const set = (key, value) => {
    if (key === "activite" && value) {
      trackEvent("quote_activity_selected", { activite: value });
    }
    if (step === 3 && ["nom", "prenom", "email", "telephone"].includes(key) && !contactTracked.current) {
      contactTracked.current = true;
      trackEvent("quote_contact_entered");
    }
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.activite) e.activite = "Sélectionnez votre activité";
    if (form.nom_entreprise.trim().length < 2) e.nom_entreprise = "Nom d'entreprise requis";
    return e;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.chiffre_affaires) e.chiffre_affaires = "Sélectionnez une tranche";
    if (!form.experience) e.experience = "Sélectionnez votre expérience";
    if (!form.antecedents) e.antecedents = "Sélectionnez une réponse";
    if (!form.nb_salaries) e.nb_salaries = "Sélectionnez un effectif";
    if (!form.sans_decennale) e.sans_decennale = "Sélectionnez une réponse";
    return e;
  };

  const validateStep3 = () => {
    const e = {};
    if (form.nom.trim().length < 2) e.nom = "Nom requis";
    if (form.prenom.trim().length < 2) e.prenom = "Prénom requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "E-mail invalide";
    if (!/^[0-9+().\s-]{8,20}$/.test(form.telephone.trim())) e.telephone = "Téléphone invalide";
    if (form.adresse.trim().length < 3) e.adresse = "Adresse requise";
    if (!/^\d{5}$/.test(form.code_postal.trim())) e.code_postal = "Code postal invalide";
    if (form.ville.trim().length < 2) e.ville = "Ville requise";
    if (!form.accepte_cgu) e.accepte_cgu = "L'acceptation des CGU est obligatoire";
    return e;
  };

  const VALIDATORS = { 1: validateStep1, 2: validateStep2, 3: validateStep3 };

  const nextStep = () => {
    const e = VALIDATORS[step]();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setFormError("Certains champs sont incomplets : vérifiez les messages ci-dessus.");
      return;
    }
    setFormError("");
    if (step === 1) trackEvent("quote_start", { activite: form.activite });
    if (step === 2) trackEvent("quote_step_2", { activite: form.activite });
    setStep(step + 1);
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    const e = validateStep3();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setFormError("Certains champs sont incomplets : vérifiez les messages ci-dessus.");
      return;
    }
    setFormError("");
    setLoading(true);
    try {
      await postToApi("/devis", {
        ...form,
        nom_entreprise: form.nom_entreprise.trim(),
        adresse: form.adresse.trim(),
        code_postal: form.code_postal.trim(),
        ville: form.ville.trim(),
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
      });
      trackEvent("quote_completed", { activite: form.activite });
      trackEvent("generate_lead", { lead_type: "devis_decennale", activite: form.activite });
      setStep(4);
      window.scrollTo({ top: 0 });
    } catch (err) {
      trackEvent("quote_error", { error_type: err.code || err.message || "unknown" });
      setFormError(submitErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="devis-page" className="border-t border-ink/15">
      <PageSeo path="/devis" />
      <div className="mx-auto grid max-w-[1440px] lg:grid-cols-12">
        {/* Left rail */}
        <aside className="border-b lg:border-b-0 lg:border-r border-ink/15 px-5 sm:px-8 py-10 lg:py-16 lg:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-4">
            Devis gratuit — sans engagement
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[min(2.5vw,2.5rem)] font-extrabold uppercase tracking-tighter leading-[0.95] mb-10">
            Votre devis<br /><span className="text-accent">{hy("décennale.")}</span>
          </h1>
          <ul className="space-y-6">
            {[
              [FileCheck, "Formulaire de 2 minutes", "RC Pro et décennale étudiées en une seule demande."],
              [ShieldCheck, "Spécialistes du BTP", "Votre activité et vos besoins étudiés par notre cabinet."],
              [Clock, "Rappel rapide", "Un conseiller spécialiste BTP vous recontacte — objectif : sous 24h ouvrées."],
            ].map(([Icon, title, text], i) => (
              <li key={i} className="flex gap-4">
                <Icon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <p className="font-display text-sm font-bold uppercase tracking-tight">{title}</p>
                  <p className="text-sm text-ink/65 mt-1">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* Form */}
        <div className="lg:col-span-8 px-5 sm:px-8 py-10 lg:py-16">
          {step < 4 && (
            <div className="mb-10">
              <div className="flex items-center justify-between border-b-2 border-ink pb-4">
                <p data-testid="devis-step-indicator" className="font-mono text-xs uppercase tracking-[0.25em]">
                  Étape <span className="text-accent">{String(step).padStart(2, "0")}</span> / 03
                </p>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink/60">
                  {STEP_LABELS[step - 1]}
                </p>
              </div>
              <div className="mt-2 h-1 w-full bg-ink/10">
                <div
                  data-testid="devis-progress"
                  className="h-1 bg-accent transition-all duration-500"
                  style={{ width: `${(step / 3) * 100}%` }}
                />
              </div>
            </div>
          )}

            {step === 1 && (
              <div key="step1" className={STEP_ANIMATION}>
                <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-8">
                  Votre métier
                </h2>
                <div className="grid gap-8 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Field label="Activité principale" required error={errors.activite} testId="devis-activite">
                      <select
                        data-testid="devis-activite"
                        className={`${inputCls} cursor-pointer`}
                        value={form.activite}
                        onChange={(e) => set("activite", e.target.value)}
                      >
                        <option value="">Sélectionnez votre métier</option>
                        {ACTIVITES.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Quel est le nom de votre entreprise ?" required error={errors.nom_entreprise} testId="devis-nom-entreprise">
                      <input
                        data-testid="devis-nom-entreprise"
                        type="text"
                        className={inputCls}
                        placeholder="Ex : SARL Dupont Bâtiment"
                        value={form.nom_entreprise}
                        onChange={(e) => set("nom_entreprise", e.target.value)}
                      />
                    </Field>
                  </div>
                </div>
                <IndicativePrice activite={form.activite} />
                <button
                  data-testid="devis-next-step"
                  onClick={nextStep}
                  className="mt-12 inline-flex w-full sm:w-auto items-center justify-center gap-3 border-2 border-ink bg-ink px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-accent hover:border-accent"
                >
                  Continuer
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {step === 2 && (
              <div key="step2" className={STEP_ANIMATION}>
                <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-8">
                  Votre profil
                </h2>
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Chiffre d'affaires annuel" required error={errors.chiffre_affaires} testId="devis-ca">
                    <select
                      data-testid="devis-ca"
                      className={`${inputCls} cursor-pointer`}
                      value={form.chiffre_affaires}
                      onChange={(e) => set("chiffre_affaires", e.target.value)}
                    >
                      <option value="">Sélectionnez une tranche</option>
                      {CA_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Votre expérience" required error={errors.experience} testId="devis-experience">
                    <select
                      data-testid="devis-experience"
                      className={`${inputCls} cursor-pointer`}
                      value={form.experience}
                      onChange={(e) => set("experience", e.target.value)}
                    >
                      <option value="">Sélectionnez votre expérience</option>
                      {EXPERIENCE_OPTIONS.map((x) => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Combien de salariés ?" required error={errors.nb_salaries} testId="devis-salaries">
                    <select
                      data-testid="devis-salaries"
                      className={`${inputCls} cursor-pointer`}
                      value={form.nb_salaries}
                      onChange={(e) => set("nb_salaries", e.target.value)}
                    >
                      <option value="">Sélectionnez un effectif</option>
                      {SALARIES_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Êtes-vous sans décennale depuis plus de 6 mois ?" required error={errors.sans_decennale} testId="devis-sans-decennale">
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="devis-sans-decennale">
                      {SANS_DECENNALE_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          data-testid={`devis-sans-decennale-${o.value}`}
                          aria-pressed={form.sans_decennale === o.value}
                          onClick={() => set("sans_decennale", o.value)}
                          className={`border-2 border-ink px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                            form.sans_decennale === o.value
                              ? "bg-ink text-paper"
                              : "bg-transparent hover:bg-smoke"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Sinistres ou résiliation au cours des 3 dernières années ?" required error={errors.antecedents} testId="devis-antecedents">
                      <select
                        data-testid="devis-antecedents"
                        className={`${inputCls} cursor-pointer`}
                        value={form.antecedents}
                        onChange={(e) => set("antecedents", e.target.value)}
                      >
                        <option value="">Sélectionnez une réponse</option>
                        {ANTECEDENTS_OPTIONS.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>
                <div className="mt-12 flex flex-wrap gap-4">
                  <button
                    data-testid="devis-prev-step"
                    onClick={() => setStep(step - 1)}
                    className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-smoke"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                  </button>
                  <button
                    data-testid="devis-next-step"
                    onClick={nextStep}
                    className="inline-flex flex-1 sm:flex-none items-center justify-center gap-3 border-2 border-ink bg-ink px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-accent hover:border-accent"
                  >
                    Continuer
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div key="step3" className={STEP_ANIMATION}>
                <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-8">
                  Vos coordonnées
                </h2>
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Civilité" required testId="devis-civilite">
                    <div className="flex gap-2 pt-1" data-testid="devis-civilite">
                      {["M.", "Mme"].map((c) => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`devis-civilite-${c === "M." ? "m" : "mme"}`}
                          aria-pressed={form.civilite === c}
                          onClick={() => set("civilite", c)}
                          className={`border-2 border-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                            form.civilite === c ? "bg-ink text-paper" : "bg-transparent hover:bg-smoke"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <div className="hidden sm:block" />
                  <Field label="Nom" required error={errors.nom} testId="devis-nom">
                    <input
                      data-testid="devis-nom"
                      type="text"
                      className={inputCls}
                      placeholder="Dupont"
                      value={form.nom}
                      onChange={(e) => set("nom", e.target.value)}
                    />
                  </Field>
                  <Field label="Prénom" required error={errors.prenom} testId="devis-prenom">
                    <input
                      data-testid="devis-prenom"
                      type="text"
                      className={inputCls}
                      placeholder="Jean"
                      value={form.prenom}
                      onChange={(e) => set("prenom", e.target.value)}
                    />
                  </Field>
                  <Field label="Mail" required error={errors.email} testId="devis-email">
                    <input
                      data-testid="devis-email"
                      type="email"
                      className={inputCls}
                      placeholder="jean.dupont@exemple.fr"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                  </Field>
                  <Field label="Téléphone" required error={errors.telephone} testId="devis-telephone">
                    <input
                      data-testid="devis-telephone"
                      type="tel"
                      className={inputCls}
                      placeholder="06 12 34 56 78"
                      value={form.telephone}
                      onChange={(e) => set("telephone", e.target.value)}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Adresse" required error={errors.adresse} testId="devis-adresse">
                      <input
                        data-testid="devis-adresse"
                        type="text"
                        className={inputCls}
                        placeholder="N° et nom de rue"
                        value={form.adresse}
                        onChange={(e) => set("adresse", e.target.value)}
                      />
                    </Field>
                  </div>
                  <Field label="Code postal" required error={errors.code_postal} testId="devis-code-postal">
                    <input
                      data-testid="devis-code-postal"
                      type="text"
                      inputMode="numeric"
                      className={inputCls}
                      placeholder="75000"
                      value={form.code_postal}
                      onChange={(e) => set("code_postal", e.target.value)}
                    />
                  </Field>
                  <Field label="Ville" required error={errors.ville} testId="devis-ville">
                    <input
                      data-testid="devis-ville"
                      type="text"
                      className={inputCls}
                      placeholder="Paris"
                      value={form.ville}
                      onChange={(e) => set("ville", e.target.value)}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <label className="flex cursor-pointer items-start gap-3 border border-ink/20 bg-white p-4">
                      <input
                        data-testid="devis-accepte-cgu"
                        type="checkbox"
                        checked={form.accepte_cgu}
                        onChange={(e) => set("accepte_cgu", e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#C73A16]"
                      />
                      <span className="text-sm leading-relaxed text-ink/75">
                        J'accepte les{" "}
                        <Link to="/cgu" target="_blank" className="underline underline-offset-4 hover:text-accent">
                          CGU
                        </Link>{" "}
                        pour l’étude de ma demande de devis.{" "}
                        <span className="text-accent">*</span>
                        <span className="mt-2 block text-xs text-ink/60">
                          Vos données servent à étudier votre demande et à vous répondre. Cette case
                          n’autorise pas la prospection de partenaires — voir notre{" "}
                          <Link to="/confidentialite" target="_blank" className="underline underline-offset-4 hover:text-accent">
                            politique de confidentialité
                          </Link>
                          .
                        </span>
                      </span>
                    </label>
                    {errors.accepte_cgu && (
                      <p data-testid="devis-accepte-cgu-error" className="mt-1.5 font-mono text-[11px] uppercase tracking-wide text-accent">
                        {errors.accepte_cgu}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-12 flex flex-wrap gap-4">
                  <button
                    data-testid="devis-prev-step"
                    onClick={() => setStep(step - 1)}
                    className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-smoke"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                  </button>
                  <button
                    data-testid="devis-submit"
                    onClick={submit}
                    disabled={loading}
                    className="inline-flex flex-1 sm:flex-none items-center justify-center gap-3 border-2 border-ink bg-accent px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none disabled:opacity-60"
                  >
                    {loading ? "Envoi en cours…" : "Demander un devis"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div
                key="step4"
                data-testid="devis-success"
                className="border-2 border-ink bg-white p-8 sm:p-12 shadow-brutal-accent animate-in fade-in slide-in-from-bottom-4 duration-500"
              >
                <CheckCircle2 className="h-12 w-12 text-accent mb-6" />
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter mb-4">
                  Demande envoyée.
                </h2>
                <p className="text-base leading-relaxed text-ink/75 mb-8 max-w-xl">
                  Merci {form.civilite} {form.nom}. Votre demande de devis RC Pro + décennale
                  pour <strong>{form.nom_entreprise}</strong> ({form.activite}) est bien
                  enregistrée. Un conseiller spécialiste du BTP vous recontactera au{" "}
                  <strong>{form.telephone}</strong> — objectif : sous 24h ouvrées.
                </p>
                <div className="grid gap-px border border-ink/15 bg-ink/15 sm:grid-cols-2 mb-10">
                  {[
                    ["Entreprise", form.nom_entreprise],
                    ["Activité", form.activite],
                    ["Chiffre d'affaires", form.chiffre_affaires],
                    ["Expérience", form.experience],
                    ["Salariés", form.nb_salaries],
                    ["Localisation", `${form.code_postal} ${form.ville}`],
                    ["E-mail", form.email],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white p-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{k}</p>
                      <p className="mt-1 text-sm font-semibold">{v}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/"
                  data-testid="devis-success-home"
                  className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-accent hover:border-accent"
                >
                  Retour à l'accueil
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
            {step < 4 && (formError || slow) && (
              <p role="alert" className="mt-6 max-w-xl text-sm leading-relaxed text-accent" data-testid="devis-form-error">
                {formError || SLOW_HINT}
              </p>
            )}
        </div>
      </div>
    </div>
  );
}
